import { Lock, Play } from 'lucide-react';
import { airingLine, badgeOfKind, isLive, looseBadges, monogram } from '@/lib/card';
import { cn } from '@/lib/cn';
import type { MediaCard } from '@/protocol/media';
import { Pill } from '../ui';

export function MediaTile({
  card,
  onSelect,
  className,
}: {
  card: MediaCard;
  onSelect: (card: MediaCard) => void;
  className?: string;
}) {
  const locked = card.playable === false;
  const live = isLive(card);
  const entitlement = badgeOfKind(card, 'entitlement');
  const airing = airingLine(card);

  return (
    <button
      type="button"
      onClick={() => onSelect(card)}
      className={cn(
        'group w-[224px] shrink-0 snap-start text-left focus-visible:outline-offset-4',
        className,
      )}
    >
      <div
        className={cn(
          'border-line bg-raised relative aspect-video overflow-hidden rounded-xl border transition',
          'group-hover:border-accent/50 group-hover:-translate-y-0.5 group-hover:shadow-2xl',
        )}
      >
        {/* The declared aspect is not reliable — payloads label 16:9 art as 2:3 — so the frame owns
            the shape and the image is cropped into it. */}
        {card.posterUrl ? (
          <img
            src={card.posterUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <span className="text-faint grid size-full place-items-center text-2xl font-semibold tracking-widest">
            {monogram(card)}
          </span>
        )}

        <span className="from-shell/90 absolute inset-0 bg-gradient-to-t via-transparent to-transparent" />

        <span className="absolute top-2 left-2 flex gap-1.5">
          {live && <Pill tone="live">Live</Pill>}
          {entitlement && <Pill tone={locked ? 'locked' : 'overlay'}>{entitlement.text}</Pill>}
        </span>

        <span
          className={cn(
            'absolute right-2 bottom-2 grid size-8 place-items-center rounded-full opacity-0 transition group-hover:opacity-100',
            locked ? 'bg-warn text-shell' : 'bg-accent text-white',
          )}
        >
          {locked ? <Lock className="size-3.5" /> : <Play className="size-3.5 fill-current" />}
        </span>
      </div>

      <p className="mt-2 truncate text-sm font-semibold tracking-tight">{card.title}</p>
      {card.subtitle && <p className="text-muted truncate text-xs">{card.subtitle}</p>}
      {airing && <p className="text-accent-soft mt-0.5 truncate text-xs">{airing}</p>}

      {looseBadges(card).length > 0 && (
        <span className="mt-1.5 flex flex-wrap gap-1">
          {looseBadges(card).map((badge) => (
            <Pill key={`${badge.kind}-${badge.text}`}>{badge.text}</Pill>
          ))}
        </span>
      )}
    </button>
  );
}
